
import React from 'react';
import { Calendar, User, Eye, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ReadingTime } from '@/components/blog/ReadingTime';

interface BlogPostMetadataProps {
  status: string;
  createdAt: string;
  scheduledAt?: string;
  author?: { first_name?: string; last_name?: string } | null;
  viewCount?: number;
  readingTimeMinutes: number;
  tags?: { id: string; name: string }[];
}

export const BlogPostMetadata = ({
  status,
  createdAt,
  scheduledAt,
  author,
  viewCount,
  readingTimeMinutes,
  tags,
}: BlogPostMetadataProps) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-2">
        <Badge variant={status === 'published' ? 'default' : (status === 'scheduled' ? 'secondary' : 'outline')}>
          {status}
        </Badge>
        {tags?.map(tag => (
          <Badge key={tag.id} variant="secondary">{tag.name}</Badge>
        ))}
      </div>
      <div className="flex flex-wrap gap-4 text-muted-foreground text-sm mt-2">
        <div className="flex items-center">
          <Calendar className="h-4 w-4 mr-1" />
          {formatDate(createdAt)}
        </div>
        {status === 'scheduled' && scheduledAt && (
          <div className="flex items-center text-amber-500 dark:text-amber-400">
            <Clock className="h-4 w-4 mr-1" />
            Scheduled for {formatDateTime(scheduledAt)}
          </div>
        )}
        <div className="flex items-center">
          <User className="h-4 w-4 mr-1" />
          {author 
            ? `${author.first_name || ''} ${author.last_name || ''}` 
            : 'Unknown Author'}
        </div>
        {viewCount !== null && (
          <div className="flex items-center">
            <Eye className="h-4 w-4 mr-1" />
            {viewCount} views
          </div>
        )}
        {readingTimeMinutes > 0 && (
          <ReadingTime minutes={readingTimeMinutes} />
        )}
      </div>
    </>
  );
};
